import { useState } from 'react'
import {
  Box,
  Button,
  Divider,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import logo from '../../logo.png'
import { ReferralVerificationData } from './types'

export interface ContactFormValues {
  email: string
  phone: string
  firstName: string
  lastName: string
  titleRole: string
  remarks: string
}

const emptyValues: ContactFormValues = {
  email: '',
  phone: '',
  firstName: '',
  lastName: '',
  titleRole: '',
  remarks: '',
}

export interface ReferralContactUpdateFormProps {
  referral: ReferralVerificationData
  trackingRef: string
  onSubmit: (values: ContactFormValues) => void
  submitLabel?: string
  children?: JSX.Element
}

export const ReferralContactUpdateForm = ({
  referral,
  trackingRef,
  onSubmit,
  submitLabel = 'Update Referral Contact',
  children,
}: ReferralContactUpdateFormProps): JSX.Element => {
  const [values, setValues] = useState<ContactFormValues>(emptyValues)

  const handleChange =
    (key: keyof ContactFormValues) =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setValues((prev) => ({ ...prev, [key]: e.target.value }))
    }

  const currentContactName = `${referral.firstName || ''} ${
    referral.lastName || ''
  }`.trim()

  return (
    <Box>
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems="flex-start"
        mb={2}
        gap={2}
      >
        <Box>
          <Typography variant="h6" color="primary.main" fontWeight={600}>
            Update Referral Contact
          </Typography>
          <Typography variant="body1" color="text.disabled">
            You are modifying the primary referral contact for{' '}
            <Box component="span" color="text.primary" fontWeight={700}>
              {referral.center?.name || 'this resource'}
            </Box>
            .
          </Typography>
        </Box>
        <Box
          component="img"
          src={logo}
          alt="resolvexLogo"
          sx={{ width: { xs: 64, sm: 96 } }}
        />
      </Box>

      <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Typography variant="caption" color="text.secondary">
          Tracking Ref: {trackingRef}
        </Typography>
        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" fontWeight="bold" color="primary">
          Current Registered Contact
        </Typography>
        <Typography variant="body2" color="text.disabled">
          {currentContactName || '-'}
          {referral.titleRole ? ` (${referral.titleRole})` : ''}
        </Typography>
        <Typography variant="body2" color="text.disabled">
          {referral.contactEmail || '-'}
        </Typography>
        <Typography variant="body2" color="text.disabled">
          {referral.contactPhone || '-'}
        </Typography>
        <Divider sx={{ my: 2 }} />

        {children}
        {children && <Divider sx={{ my: 2 }} />}

        <Typography
          variant="subtitle1"
          fontWeight="bold"
          color="primary"
          mb={2}
        >
          New Contact
        </Typography>
        <Box display="flex" flexDirection="column" gap={2}>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={2}
          >
            <TextField
              fullWidth
              label="Email"
              value={values.email}
              onChange={handleChange('email')}
            />
            <TextField
              fullWidth
              label="Phone"
              value={values.phone}
              onChange={handleChange('phone')}
            />
          </Box>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={2}
          >
            <TextField
              fullWidth
              label="First name"
              value={values.firstName}
              onChange={handleChange('firstName')}
            />
            <TextField
              fullWidth
              label="Last name"
              value={values.lastName}
              onChange={handleChange('lastName')}
            />
          </Box>
          <TextField
            fullWidth
            label="Position / Title"
            value={values.titleRole}
            onChange={handleChange('titleRole')}
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Remarks / Notes"
            value={values.remarks}
            onChange={handleChange('remarks')}
          />
          <Button
            variant="contained"
            size="large"
            onClick={() => onSubmit(values)}
          >
            {submitLabel}
          </Button>
        </Box>
      </Paper>
    </Box>
  )
}
